import { Injectable } from '@angular/core';
import { DynamicService } from './dynamic.service';
import { Bi } from '@entities/bi.class';
import { db } from '@database/db';
import { SEUIL_ECRITURE_LOCALE_MS, enregistrerEcritureLocaleLente, enregistrerErreurTelemetrie } from '@services/core/telemetrie';

/**
 * Dossiers en mémoire LIBÉRÉS : en quittant le rapport, rapport-edit vide leurs listes
 * (phrases, photos, investigations, dommages) pour rendre la mémoire. Écrire un tel objet
 * dans la copie locale effacerait le dossier de l'appareil — ce que faisaient plusieurs
 * enregistrements tardifs (changement de dommage lent, rendu de l'éditeur, photos
 * manquantes…), d'où des phrases présentes côté serveur et absentes de l'appareil (07/10).
 */
const dossiersLiberes = new WeakSet<object>();

export function marquerDossierLibere(bi: any): void {
	if (bi && typeof bi === 'object') {
		dossiersLiberes.add(bi);
		if (bi.rapport && typeof bi.rapport === 'object') dossiersLiberes.add(bi.rapport);
	}
}

export function estDossierLibere(bi: any): boolean {
	return !!bi && typeof bi === 'object'
		&& (dossiersLiberes.has(bi) || (!!bi.rapport && typeof bi.rapport === 'object' && dossiersLiberes.has(bi.rapport)));
}

/**
 * Poids approximatif (Ko) d'un objet sérialisé. Les dossiers contiennent des références
 * croisées (phrase → rapport → phrases…) sur lesquelles JSON.stringify échoue : chaque
 * objet n'est compté qu'une fois.
 */
function estimerPoidsKo(objet: any): number | undefined {
	try {
		const vus = new WeakSet<object>();
		const texte = JSON.stringify(objet, (_cle, valeur) => {
			if (valeur && typeof valeur === 'object') {
				if (vus.has(valeur)) return undefined;
				vus.add(valeur);
			}
			return valeur;
		});
		return Math.round((texte?.length ?? 0) / 1024);
	} catch {
		return undefined;
	}
}

@Injectable({
	providedIn: 'root'
})
export class BiService extends DynamicService<Bi> {
	protected override entityName:string = 'Bi';
	override assignClass(entity: Bi): Bi {
		this.entityName = 'Bi';
		return Object.assign(new Bi(), entity);
	}

	/**
	 * Ajoute un commentaire dans la copie locale du dossier (ciblé). Les commentaires
	 * envoyés depuis la liste (envoi au BE / au BA) partaient au serveur et à l'ombre,
	 * mais n'apparaissaient pas dans le dossier de l'appareil avant la resynchro (07/10).
	 */
	async ajouterCommentaireLocal(biId: number, commentaire: any): Promise<void> {
		try {
			await db.table('bi').where(':id').equals(biId).modify((bi: any) => {
				if (!bi?.rapport) return;
				const liste = Array.isArray(bi.rapport.commentaires) ? bi.rapport.commentaires : [];
				if (!liste.some((c: any) => c?.id === commentaire?.id)) liste.push({ ...commentaire });
				bi.rapport.commentaires = liste;
			});
		} catch { /* copie locale absente : la resynchro l'apportera */ }
	}

	/** Écriture complète du dossier : jamais d'un dossier libéré (vidé en quittant le rapport). */
	override async localPatchEntity(entity: any, snackBar: Boolean = false): Promise<void> {
		if (estDossierLibere(entity)) {
			console.warn(`🛡️ Dossier ${entity?.id} libéré (rapport quitté) : écriture locale ignorée`);
			enregistrerErreurTelemetrie(new Error('Écriture d\'un dossier libéré ignorée'), 'dossier.ecriture_dossier_libere', { 'bi.id': Number(entity?.id) || 0 });
			return;
		}
		return super.localPatchEntity(entity, snackBar);
	}

	/**
	 * Met à jour des PHOTOS dans la copie locale du dossier (même principe que
	 * majPhraseLocale). Renvoie faux si l'une d'elles n'y figure pas : l'appelant se
	 * rabat alors sur l'écriture complète (reloadBi), comme avant.
	 */
	async majPhotosLocales(biId: number, majs: { id: string; champs: Record<string, any> }[]): Promise<boolean> {
		if (majs.length === 0) return true;
		const debut = performance.now();
		let trouvees = 0;
		try {
			await db.table('bi').where(':id').equals(biId).modify((bi: any) => {
				const photos: any[] = bi?.rapport?.photos || [];
				for (const maj of majs) {
					const photo = photos.find(p => p?.id === maj.id);
					if (!photo) continue;
					Object.assign(photo, maj.champs);
					trouvees++;
				}
			});
		} catch (error) {
			console.warn('⚠️ Mise à jour ciblée des photos impossible, repli sur l\'écriture complète:', error);
			return false;
		}
		const duree = performance.now() - debut;
		if (duree > SEUIL_ECRITURE_LOCALE_MS) {
			enregistrerEcritureLocaleLente('bi.photos', duree, { 'bi.id': biId, 'photos.nombre': majs.length });
		}
		return trouvees === majs.length;
	}

	/** Dossiers dont le poids de la copie locale a déjà été mesuré (une fois par session). */
	private static poidsMesures = new Set<number>();

	/**
	 * Met à jour UNE phrase dans la copie locale du dossier, au lieu de réécrire tout le
	 * dossier (localPatchEntity). Mêmes données, enregistrées au même moment : aucune
	 * fenêtre de perte.
	 *
	 * La base locale (IndexedDB) stocke le dossier d'un seul bloc — rapport, phrases,
	 * photos — et ne sait pas modifier un champ à l'intérieur : elle relit et réécrit le
	 * bloc. On évite en revanche le parcours et la recopie de tout l'objet en mémoire, qui
	 * étaient refaits à chaque pause de frappe dans le wizard et figeaient les iPad sur les
	 * gros dossiers (Xavier Charraud, 129 photos, 25/09).
	 *
	 * Renvoie faux si la phrase n'est pas dans la copie locale : l'appelant se rabat alors
	 * sur l'écriture complète.
	 */
	/**
	 * Met à jour des champs du RAPPORT dans la copie locale du dossier, sans réécrire tout
	 * le dossier (même principe que majPhraseLocale). Renvoie faux si le dossier est absent.
	 */
	async majRapportLocal(biId: number, champs: Record<string, any>): Promise<boolean> {
		let trouve = false;
		try {
			await db.table('bi').where(':id').equals(biId).modify((bi: any) => {
				if (!bi?.rapport) return;
				Object.assign(bi.rapport, champs);
				trouve = true;
			});
		} catch (error) {
			console.warn('⚠️ Mise à jour locale du rapport impossible :', error);
			return false;
		}
		return trouve;
	}

	async majPhraseLocale(biId: number, phraseId: string, champs: Record<string, any>): Promise<boolean> {
		const debut = performance.now();
		let trouvee = false;
		let poidsKo: number | undefined;
		const mesurer = !BiService.poidsMesures.has(biId);
		try {
			await db.table('bi').where(':id').equals(biId).modify((bi: any) => {
				const phrase = bi?.rapport?.phrases?.find((p: any) => p?.id === phraseId);
				if (!phrase) return;
				Object.assign(phrase, champs);
				trouvee = true;
				if (mesurer) {
					poidsKo = estimerPoidsKo(bi);
				}
			});
		} catch (error) {
			console.warn('⚠️ Mise à jour ciblée de la phrase impossible, repli sur l\'écriture complète:', error);
			return false;
		}
		const duree = performance.now() - debut;
		if (poidsKo !== undefined) BiService.poidsMesures.add(biId);
		if (duree > SEUIL_ECRITURE_LOCALE_MS || poidsKo !== undefined) {
			enregistrerEcritureLocaleLente('bi.phrase', duree, {
				'bi.id': biId,
				...(poidsKo !== undefined ? { 'bi.poids_ko': poidsKo } : {}),
			});
		}
		return trouvee;
	}
}